import { ChatComponentType } from '@common/hooks/useChatComponentActions';

export const DEFAULT_PAGINATION = { limit: 10, skip: 0, total: 0 };
export const MAX_FILES_COUNT = 5; // maximum upload 5 files in a messages
export const MAX_INPUT_LENGTH = 2000;

// --- MIK-4828: file-attachment availability --------------------------------
// The element offers three modes; `conditional` defers to a Boolean field on the
// Message Group table. Shared by the v3 and v4 ChatComponent (v4 imports this
// module directly) so the two can never disagree.
export const CHAT_UPLOAD_FILE_MODE = {
  ALWAYS: 'always',
  NEVER: 'never',
  CONDITIONAL: 'conditional',
} as const;

/**
 * Read the configured mode, tolerating elements saved before MIK-4828.
 *
 * Those carry the old `supportUploadFile` boolean instead, where `undefined`
 * also meant "on" -- so the legacy fallback is `!== false`, matching the
 * behaviour this option had before the third mode existed.
 */
export const getUploadFileMode = (
  attributes: {
    uploadFileSetting?: ChatComponentType.ChatUploadFileSettingType;
    supportUploadFile?: boolean;
  } = {}
): ChatComponentType.ChatUploadFileMode => {
  const mode = attributes.uploadFileSetting?.mode;
  if (
    mode === CHAT_UPLOAD_FILE_MODE.ALWAYS ||
    mode === CHAT_UPLOAD_FILE_MODE.NEVER ||
    mode === CHAT_UPLOAD_FILE_MODE.CONDITIONAL
  ) {
    return mode;
  }
  return attributes.supportUploadFile !== false
    ? CHAT_UPLOAD_FILE_MODE.ALWAYS
    : CHAT_UPLOAD_FILE_MODE.NEVER;
};

/**
 * Whether the file picker is available for the chatroom currently open.
 *
 * In `conditional` mode the group's Boolean field is a *disable* flag
 * (MIK-4828: `true` hides the picker, `false` -- the column default -- shows
 * it), so an unset or not-yet-selected group keeps uploads available.
 */
export const isUploadFileSupported = ({
  attributes,
  selectedGroup,
}: {
  attributes?: {
    uploadFileSetting?: ChatComponentType.ChatUploadFileSettingType;
    supportUploadFile?: boolean;
  };
  selectedGroup?: Pick<
    ChatComponentType.GroupMessageType,
    'uploadFileDisabled'
  > | null;
}): boolean => {
  const mode = getUploadFileMode(attributes);
  if (mode === CHAT_UPLOAD_FILE_MODE.NEVER) {
    return false;
  }
  if (mode === CHAT_UPLOAD_FILE_MODE.CONDITIONAL) {
    return !selectedGroup?.uploadFileDisabled;
  }
  return true;
};

/**
 * fid to ask the server for, or `undefined` when the element does not depend on
 * a chatroom field -- the other two modes need no per-group data.
 */
export const getUploadFileDisabledFieldFid = (
  attributes: {
    uploadFileSetting?: ChatComponentType.ChatUploadFileSettingType;
    supportUploadFile?: boolean;
  } = {}
): string | undefined =>
  getUploadFileMode(attributes) === CHAT_UPLOAD_FILE_MODE.CONDITIONAL
    ? attributes.uploadFileSetting?.disableField?.fid || undefined
    : undefined;

/**
 * Everything the ChatComponent needs for the file-attachment rule, in one call
 * shared by the v3 and v4 hooks.
 *
 * `selectedGroup` is the snapshot taken when the room was opened, so in
 * `conditional` mode the flag is re-read off the polled group list instead --
 * otherwise flipping the Boolean in the database would never reach an open
 * session. Falls back to the snapshot if the room is no longer in the list.
 */
export const resolveUploadFileSupport = ({
  attributes,
  selectedGroup,
  groupMessages,
}: {
  attributes?: {
    uploadFileSetting?: ChatComponentType.ChatUploadFileSettingType;
    supportUploadFile?: boolean;
  };
  selectedGroup?: Pick<
    ChatComponentType.GroupMessageType,
    'id' | 'uploadFileDisabled'
  > | null;
  groupMessages?: Pick<
    ChatComponentType.GroupMessageType,
    'id' | 'uploadFileDisabled'
  >[];
}): {
  supportUploadFile: boolean;
  uploadFileDisabledFieldFid?: string;
} => {
  const mode = getUploadFileMode(attributes);
  // Only the conditional mode looks at group data; the other two skip the lookup.
  const group =
    mode === CHAT_UPLOAD_FILE_MODE.CONDITIONAL && selectedGroup
      ? (groupMessages || []).find((e) => e.id === selectedGroup.id) ||
        selectedGroup
      : selectedGroup;
  return {
    supportUploadFile: isUploadFileSupported({
      attributes,
      selectedGroup: group,
    }),
    uploadFileDisabledFieldFid: getUploadFileDisabledFieldFid(attributes),
  };
};

export const isPCView = (width: number) => width > 768;
export const PC_WIDTH_GROUP_MESSAGE_VIEW = 325;
export const CHAT_DUMMY_DATA: {
  ChatGroup: ChatComponentType.GroupMessageTypeWithMessageJson[];
  ChatMessages: ChatComponentType.ChatMessagesTypeWithMessageJson[];
  UnReadGroupStatus: Record<string, number>;
} = {
  ChatGroup: [
    {
      id: '1',
      latestMessage: {
        id: '0',
        message: {
          text: 'テキストが入りますテキストが入りますテキストが入ります',
        },
        senderAvatar: {
          url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/example_avatar_1.png',
        },
        senderId: '1',
        senderName: 'ユーザー名',
        createdAt: '2025-01-01 12:00:00',
      },
      groupName: 'ユーザー名',
      groupImage: {
        url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/logo.png',
      },
      numberOfUsers: 1,
    },
    {
      id: '2',
      latestMessage: {
        id: '0',
        message: {
          text: 'テキストが入りますテキストが入りますテキストが入ります',
        },
        senderAvatar: {
          url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/example_avatar_2.jpeg',
        },
        senderId: '2',
        senderName: 'ユーザー名',
        createdAt: '2025-02-04 12:00:00',
      },
      groupName: 'ユーザー名',
      groupImage: {
        url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/example_avatar_2.jpeg',
      },
      numberOfUsers: 2,
    },
    {
      id: '3',
      latestMessage: {
        id: '0',
        message: {
          text: 'テキストが入りますテキストが入りますテキストが入ります',
        },
        senderAvatar: {
          url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/example_avatar_3.jpeg',
        },
        senderId: '3',
        senderName: 'ユーザー名',
        createdAt: '2025-02-13 12:00:00',
      },
      groupName: 'ユーザー名',
      groupImage: {
        url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/logo.png',
      },
      numberOfUsers: 3,
    },
    {
      id: '4',
      latestMessage: {
        id: '0',
        message: {
          text: 'テキストが入りますテキストが入りますテキストが入ります',
        },
        senderAvatar: {
          url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/example_avatar_4.jpeg',
        },
        senderId: '3',
        senderName: 'ユーザー名',
        createdAt: '2025-02-14 12:00:00',
      },
      groupName: 'ユーザー名',
      groupImage: {
        url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/logo.png',
      },
      numberOfUsers: 100,
    },
  ],
  UnReadGroupStatus: {
    '1': 1,
    '2': 100,
    '3': 56,
  },
  ChatMessages: [
    {
      id: '1',
      message: {
        text: 'テキストが入りますテキストが入りますテキストが入りますテキストが入りますテキストが入ります',
        files: [
          {
            url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/logo.png',
            type: 'image/png',
            filename: 'example_avatar_1.png',
          },
          {
            url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/logo.png',
            filename: 'file1.docx',
            type: 'application/text',
          },
          {
            url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/logo.png',
            filename: 'file2_super_long_long_long.docx',
            type: 'application/text',
          },
          {
            url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/logo.png',
            type: 'image/jpeg',
            filename: 'example_avatar_4.jpeg',
          },
        ],
      },
      senderAvatar: {
        url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/example_avatar_4.jpeg',
      },
      senderId: '2',
      senderName: 'ユーザー名',
      createdAt: '2025/02/03 12:30:00',
      groupId: '1',
    },
    {
      id: '2',
      message: {
        text: 'テキストが入ります',
        files: [
          {
            url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/logo.png',
            filename: 'file1.docx',
            type: 'application/text',
          },
          {
            url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/logo.png',
            type: 'application/text',
            filename: 'file2_super_long_long_long.docx',
          },
        ],
      },
      senderAvatar: {
        url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/example_avatar_4.jpeg',
      },
      senderId: '2',
      senderName: 'ユーザー名',
      createdAt: '2025/02/03 12:32:00',
      groupId: '1',
    },
    {
      id: '3',
      message: {
        text: 'テキストが入ります',
      },
      senderAvatar: {
        url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/example_avatar_1.png',
      },
      senderId: '1',
      senderName: 'ユーザー名',
      createdAt: '2025/02/03 15:00:00',
      groupId: '1',
    },
    {
      id: '4',
      message: {
        text: 'テキストが入ります',
      },
      senderAvatar: {
        url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/example_avatar_4.jpeg',
      },
      senderId: '2',
      senderName: 'ユーザー名',
      createdAt: '2025/02/03 15:00:00',
      groupId: '1',
    },
    {
      id: '5',
      message: {
        text: 'テキストが入りますテキストが入りますテキストが入りますテキストが入りますテキストが入りますテキストが入りますテキストが入りますテキスト',
      },
      senderAvatar: {
        url: 'https://click-assets.s3.ap-northeast-1.amazonaws.com/example_avatar_1.png',
      },
      senderId: '1',
      senderName: 'ユーザー名',
      createdAt: '2025/02/03 13:24:00',
      groupId: '1',
    },
  ],
};
export const FILE_BACKGROUND_URL =
  'https://click-assets.s3.ap-northeast-1.amazonaws.com/file_placeholder.png';
export const DEFAULT_AVATAR_URL =
  'https://click-assets.s3.ap-northeast-1.amazonaws.com/default_avatar.jpeg';
export const FILE_UPLOADING_TYPE = 'FILE_UPLOADING_TYPE';
export const FILE_UPLOADING: ChatComponentType.FileType = {
  filename: 'Uploading...',
  type: FILE_UPLOADING_TYPE,
  url: FILE_BACKGROUND_URL,
};
export const getUrlRegex = () => new RegExp(/(https?:\/\/[^\s]+)/g);

export const getNumberOverflow = (
  value?: number,
  options?: { digit: number }
) => {
  const { digit } = options || { digit: 2 };
  if (value === undefined || Number.isNaN(Number(value))) {
    return undefined;
  }
  const maxNumber = Math.pow(10, digit);
  if (value >= maxNumber) {
    return `${maxNumber - 1}+`;
  }
  return `${value}`;
};

export const isNumberOfUserDisplayed = ({
  numberOfUsers,
}: {
  numberOfUsers?: number;
}) => {
  return numberOfUsers ? Number(numberOfUsers) !== 2 : false;
};
